/*
 * hdr-variant-availability.js — contextuele variant-optie-disabling voor Dawn <variant-selects>.
 *
 * Probleem (QA 2026-06-15): bij een sparse variant-matrix (niet elke Maat heeft elke
 * Beglazing/Glas) blijven onmogelijke optie-waarden klikbaar. Dawn snapt dan stil terug
 * naar een geldige waarde — verwarrend dead-end. Zie glas-bucket-B (Velux dakramen) waar
 * Geluid/Inbraak maar voor sommige maten leverbaar zijn.
 *
 * Oplossing: cascading availability. Optie 1 (Maat) blijft volledig open; latere opties
 * (Beglazing/Glas/Afwerking) worden gefilterd op de eerder gekozen waarden. Onbereikbare
 * waarden worden `disabled` (radio + <option>) en visueel gedimd. Wordt de huidige keuze
 * van een latere optie onbereikbaar na een Maat-wissel, dan schuift hij naar de eerste
 * bereikbare waarde en krijgt Dawn één change-event om prijs/URL/media bij te werken.
 *
 * Moderne Dawn vervangt het hele <variant-selects>-element bij elke wissel
 * (HTMLUpdateUtility.viewTransition) — daarom her-passen we via een MutationObserver na
 * elke re-render i.p.v. eenmalig te initialiseren. De oude kopie blijft daarbij 500 ms
 * verborgen staan; die slaan we over (28-09-2026, @d8f0da: gemeten 30 tot 539 change-events
 * uit die verborgen kopie na een maatwissel, meetlat tools/verify/meet_variantkeuze_storm.cjs).
 *
 * Puur additief: disablet ALLEEN combinaties die als variant niet bestaan. Uitverkochte-
 * maar-bestaande varianten blijven selecteerbaar (Dawn's "uitverkocht"-UX intact).
 * Matrix = [["<maat>","<beglazing>"], ...] embedded via product-variant-picker.liquid.
 */
(function () {
  'use strict';

  var STYLE_ID = 'hdr-variant-availability-style';
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.product-form__input input[type="radio"].hdr-opt-unavailable + label,' +
      '.product-form__input input[type="radio"]:disabled + label{' +
      'opacity:.38;cursor:not-allowed;text-decoration:line-through;}' +
      '.product-form__input select option:disabled{color:#9aa6b2;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function getMatrix(vs) {
    var el = vs.querySelector('script[data-variant-matrix]');
    if (!el) return null;
    try {
      var m = JSON.parse(el.textContent);
      return (Array.isArray(m) && m.length) ? m : null;
    } catch (e) { return null; }
  }

  // Optie-groepen in DOM-volgorde = variant.options-volgorde (positie 1, 2, 3).
  function getGroups(vs) {
    var groups = [];
    vs.querySelectorAll('.product-form__input').forEach(function (el) {
      var radios = el.querySelectorAll('input[type="radio"]');
      if (radios.length) {
        groups.push({ type: 'radio', inputs: Array.prototype.slice.call(radios) });
      } else {
        var sel = el.querySelector('select');
        if (sel) groups.push({ type: 'select', select: sel });
      }
    });
    return groups;
  }

  function selectedValues(groups) {
    return groups.map(function (g) {
      if (g.type === 'radio') {
        var c = g.inputs.filter(function (i) { return i.checked; })[0];
        return c ? c.value : null;
      }
      return g.select.value;
    });
  }

  // Bestaat er een variant met optie[idx]==value én optie[j]==sel[j] voor alle j<idx?
  function exists(matrix, idx, value, sel) {
    return matrix.some(function (row) {
      if (row[idx] !== value) return false;
      for (var j = 0; j < idx; j++) {
        if (sel[j] != null && row[j] !== sel[j]) return false;
      }
      return true;
    });
  }

  // Disable onbereikbare waarden + corrigeer ongeldige/lege selectie.
  // Return: het element van de eerste gecorrigeerde optie (aangevinkte radio of select), anders null.
  function applyTo(vs) {
    var matrix = getMatrix(vs);
    if (!matrix) return null;
    var groups = getGroups(vs);
    if (!groups.length) return null;
    var sel = selectedValues(groups);
    var fixedIdx = -1;
    for (var pass = 0; pass <= groups.length; pass++) {
      var anyThisPass = false;
      for (var idx = 0; idx < groups.length; idx++) {
        var g = groups[idx];
        var reachable = [];
        if (g.type === 'radio') {
          g.inputs.forEach(function (inp) {
            var ok = exists(matrix, idx, inp.value, sel);
            inp.disabled = !ok;
            inp.classList.toggle('hdr-opt-unavailable', !ok);
            if (ok) reachable.push(inp.value);
          });
        } else {
          Array.prototype.forEach.call(g.select.options, function (opt) {
            var ok = exists(matrix, idx, opt.value, sel);
            opt.disabled = !ok;
            if (ok) reachable.push(opt.value);
          });
        }
        var current = sel[idx];
        var needFix = reachable.length > 0 && (current == null || reachable.indexOf(current) === -1);
        if (needFix) {
          sel[idx] = reachable[0];
          if (g.type === 'radio') {
            g.inputs.forEach(function (inp) { inp.checked = (inp.value === sel[idx]); });
          } else {
            g.select.value = sel[idx];
            // Dawn bouwt zijn verzoek uit option[selected] (het attribuut), niet uit select.value,
            // en werkt dat attribuut alleen bij op het element waar ons change-event landt. Zonder
            // dit vroeg Dawn de oude waarde terug en corrigeerden we eindeloos (@d8f0da, Kleur-
            // dropdown bij rolgordijnen). Precies een optie houdt het attribuut: Dawn's eigen
            // updateSelectionMetadata gaat stuk als er geen enkele is.
            Array.prototype.forEach.call(g.select.options, function (opt) {
              if (opt.value === sel[idx]) opt.setAttribute('selected', 'selected');
              else opt.removeAttribute('selected');
            });
          }
          if (fixedIdx === -1) fixedIdx = idx;
          anyThisPass = true;
        }
      }
      if (!anyThisPass) break;
    }
    if (fixedIdx === -1) return null;
    var fg = groups[fixedIdx];
    if (fg.type === 'select') return fg.select;
    return fg.inputs.filter(function (i) { return i.checked; })[0] || null;
  }

  var busy = false;
  function applyAll() {
    if (busy) return;
    busy = true;
    var toDispatch = null;
    document.querySelectorAll('variant-selects').forEach(function (vs) {
      // viewTransition laat de oude kopie nog 500 ms staan op display:none, ACHTER de nieuwe.
      // Die niet bewerken: zijn keuze is verouderd, en een change-event van daaruit vraagt
      // Dawn om een render van die oude keuze, wat een reeks renders op gang bracht (@d8f0da).
      if (vs.style.display === 'none') return;
      var ctl = applyTo(vs);
      if (ctl) toDispatch = ctl;
    });
    busy = false;
    if (toDispatch) {
      // Eén change-event zodat Dawn de gecorrigeerde variant (prijs/URL/media) oppakt, en wel
      // op het element dat we ZELF veranderden. Een event op een onaangeroerde select (de Maat)
      // laat Dawn daar het selected-attribuut weghalen; een vers gerenderde select valt dan
      // terug op zijn eerste optie en de klant verliest zijn maatkeuze (@d8f0da, FMC MK12->CK02).
      toDispatch.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      scheduled = false;
      applyAll();
    }, 0);
  }

  function boot() {
    injectStyle();
    applyAll();
    // Dawn vervangt <variant-selects> bij elke wissel → her-pas na childList-mutaties.
    var obs = new MutationObserver(function (muts) {
      if (busy) return; // negeer onze eigen toggles (attribuut-only; childList van Dawn telt wel)
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].type === 'childList' && (muts[i].addedNodes.length || muts[i].removedNodes.length)) {
          schedule();
          return;
        }
      }
    });
    var roots = document.querySelectorAll('product-info');
    if (roots.length) {
      roots.forEach(function (r) { obs.observe(r, { childList: true, subtree: true }); });
    } else {
      var main = document.querySelector('main') || document.body;
      obs.observe(main, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
